import TOP10A_DYNAMIC_FILTERS_CONFIG from '../Dashboarding.constants/TOP10A_DYNAMIC_FILTERS_CONFIG'

// Types et constantes
const ENTITY_TYPES = {
  WGE: "WGE",
  WGUK: "WGUK",
}

const PERIOD_TYPES = {
  FY: "FY",
  YTD: "YTD",
}

// Validation des filtres
function validateFilters(filters) {
  const { entity, period, products, countries, clients } = filters
  
  if (entity && !Object.values(ENTITY_TYPES).includes(entity)) {
    throw new Error(`Invalid entity: ${entity}. Must be one of: ${Object.values(ENTITY_TYPES).join(', ')}`)
  }
  
  if (period && !Object.values(PERIOD_TYPES).includes(period)) {
    throw new Error(`Invalid period: ${period}. Must be one of: ${Object.values(PERIOD_TYPES).join(', ')}`)
  }
  
  if (!Array.isArray(products)) {
    throw new Error('Products must be an array')
  }
  
  if (!Array.isArray(countries)) {
    throw new Error('Countries must be an array')
  }
  
  if (!Array.isArray(clients)) {
    throw new Error('Clients must be an array')
  }
  
  return true
}

// Validation des données brutes
function validateRawData(rawData) {
  if (!Array.isArray(rawData)) {
    throw new Error('Raw data must be an array')
  }
  
  if (rawData.length === 0) {
    console.warn('Raw data is empty')
    return false
  }
  
  // Vérifier les champs requis
  const requiredFields = ['order_invoice_date', 'order_item_amount_cents_cons_eur', 'buyer_country']
  const sampleRow = rawData[0]
  
  for (const field of requiredFields) {
    if (!(field in sampleRow)) {
      throw new Error(`Missing required field: ${field}`)
    }
  }
  
  return true
}

// Filtrage de base (entité et période)
function applyBaseFilters(rawData, filters) {
  const { entity = "WGE", period = "FY" } = filters
  
  return rawData.filter(row => {
    // Filtre entité
    if (entity && row.order_type_sales !== ENTITY_TYPES[entity]) {
      return false
    }
    
    // Règles métier spécifiques
    if (entity === "WGE" && row.buyer_country === "United Kingdom") {
      return false
    }
    if (entity === "WGUK" && row.buyer_country !== "United Kingdom") {
      return false
    }
    
    // Filtre période
    if (period === "YTD") {
      const currentYear = new Date().getFullYear()
      const rowYear = new Date(row.order_invoice_date).getFullYear()
      if (rowYear !== currentYear) {
        return false
      }
    }
    
    return true
  })
}

// Filtrage par dimension
function applyDimensionFilter(data, dimension, selectedValues) {
  if (!selectedValues || selectedValues.length === 0) {
    return data
  }
  
  const fieldMapping = {
    products: 'product_name',
    countries: 'buyer_country',
    clients: (row) => row.buyer_p3 || row.buyer_name || "Unknown Client"
  }
  
  const field = fieldMapping[dimension]
  if (!field) {
    throw new Error(`Unknown dimension: ${dimension}`)
  }
  
  return data.filter(row => {
    const value = typeof field === 'function' ? field(row) : row[field]
    return selectedValues.includes(value)
  })
}

// Calcul des années disponibles
function calculateYears(data) {
  const years = [...new Set(data.map(row => 
    new Date(row.order_invoice_date).getFullYear()
  ))].sort()
  
  return {
    THIS_YEAR: years[years.length - 1]?.toString(),
    LAST_YEAR: years[years.length - 2]?.toString(),
    allYears: years
  }
}

// Fonction utilitaire pour calculer la variation
function calculateVariation(thisYear, lastYear) {
  if (!lastYear || lastYear === 0) return null
  return ((thisYear - lastYear) / lastYear) * 100
}

// Agrégation générique pour une dimension
function aggregateByDimension(data, dimension, years, options = {}) {
  const { 
    valueField = 'order_item_amount_cents_cons_eur',
    nameField = dimension,
    imageField = null,
    limit = 10,
    sortBy = years.THIS_YEAR
  } = options
  
  const aggregationMap = {}
  
  data.forEach(row => {
    const year = new Date(row.order_invoice_date).getFullYear().toString()
    if (year !== years.THIS_YEAR && year !== years.LAST_YEAR) return
    
    const key = typeof nameField === 'function' ? nameField(row) : row[nameField] || `Unknown ${dimension}`
    
    if (!aggregationMap[key]) {
      aggregationMap[key] = { 
        [dimension]: key,
        [years.THIS_YEAR]: 0, 
        [years.LAST_YEAR]: 0 
      }
      
      if (imageField && row[imageField]) {
        aggregationMap[key].image = row[imageField]
      }
    }
    
    const value = parseFloat(row[valueField]) || 0
    if (year === years.THIS_YEAR) aggregationMap[key][years.THIS_YEAR] += value
    if (year === years.LAST_YEAR) aggregationMap[key][years.LAST_YEAR] += value
  })
  
  return Object.values(aggregationMap)
    .map(row => {
      const thisYear = row[years.THIS_YEAR] || 0
      const lastYear = row[years.LAST_YEAR] || 0
      return {
        ...row,
        Total: thisYear,
        varPct: calculateVariation(thisYear, lastYear),
      }
    })
    .sort((a, b) => b[sortBy] - a[sortBy])
    .slice(0, limit)
}

// Fonction utilitaire pour appliquer les filtres dynamiques selon la configuration
function applyDynamicFilters(data, section, filters) {
  const config = TOP10A_DYNAMIC_FILTERS_CONFIG[section]
  if (!config) return data

  return data.filter(row => {
    // Pour chaque type de filtre configuré pour cette section
    for (const filterType of config.filters) {
      const selectedValues = filters[filterType]
      if (!selectedValues || selectedValues.length === 0) continue

      const mapping = config.filterMapping[filterType]
      if (!mapping) continue

      const value = typeof mapping.field === 'function' 
        ? mapping.field(row) 
        : row[mapping.field]

      if (!selectedValues.includes(value)) return false
    }
    return true
  })
}

// Transforme les données brutes PostgreSQL en collection pour Top10A
function buildTop10ACollection(rawData, filters = {}) {
  const {
    entity = "WGE", // WGE ou WGUK
    currency = "C€", // C€, €, £, $, C$
    period = "FY", // FY ou YTD
    products = [], // Array de produits sélectionnés
    countries = [], // Array de pays sélectionnés
    clients = [], // Array de clients sélectionnés
    limit = 10,
  } = filters

  // Mapping des filtres
  const entityMapping = {
    WGE: "WGE",
    WGUK: "WGUK",
  }

  const currencyMapping = {
    'C€': 'eur',
    '€': 'eur',
    '£': 'gbp',
    '$': 'usd',
    'C$': 'cad'
  }

  // Filtrer les données selon les critères de base
  const baseFilteredData = rawData.filter(row => {
    // Filtre entité (WGE/WGUK/WGALL)
    if (entity === "WGALL") {
      // Pour WGALL, accepter à la fois WGE et WGUK
      if (row.order_type_sales !== "WGE" && row.order_type_sales !== "WGUK") {
        return false
      }
    } else if (entity && row.order_type_sales !== entityMapping[entity]) {
      return false
    }

    // Filtre devise
    if (currency && currencyMapping[currency]) {
      const targetCurrency = currencyMapping[currency]
      if (row.order_currency !== targetCurrency) {
        return false
      }
    }

    // Filtre période (FY vs YTD)
    if (period === "YTD") {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth() // 0-11
      
      const rowDate = new Date(row.order_invoice_date)
      const rowYear = rowDate.getFullYear()
      const rowMonth = rowDate.getMonth() // 0-11
      
      // YTD = (année actuelle OU année précédente) ET mois <= mois actuel
      if ((rowYear !== currentYear && rowYear !== currentYear - 1) || rowMonth > currentMonth) {
        return false
      }
    }
    return true
  })

  // Calculer les années (cette année et dernière année)
  let THIS_YEAR, LAST_YEAR
  
  if (period === "YTD") {
    // Pour YTD, on utilise l'année actuelle et l'année précédente
    const currentDate = new Date()
    THIS_YEAR = currentDate.getFullYear().toString()
    LAST_YEAR = (currentDate.getFullYear() - 1).toString()
  } else {
    // Pour FY, on prend les années disponibles dans les données
    const years = [...new Set(baseFilteredData.map(row => 
      new Date(row.order_invoice_date).getFullYear()
    ))].sort()
    
    THIS_YEAR = years[years.length - 1]?.toString()
    LAST_YEAR = years[years.length - 2]?.toString()
  }

  // Fonction pour calculer la variation
  function getVarPct(thisYear, lastYear) {
    if (!lastYear || lastYear === 0) return null
    return ((thisYear - lastYear) / lastYear) * 100
  }

  // Fonction pour filtrer les données YTD
  function filterYTDData(data, targetYear) {
    if (period !== "YTD") return data
    
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth() // 0-11
    
    return data.filter(row => {
      const rowDate = new Date(row.order_invoice_date)
      const rowYear = rowDate.getFullYear()
      const rowMonth = rowDate.getMonth() // 0-11
      
      // Si c'est l'année actuelle, filtrer par mois
      if (rowYear === currentDate.getFullYear()) {
        return rowMonth <= currentMonth
      }
      // Si c'est l'année précédente, filtrer par la même période
      if (rowYear === currentDate.getFullYear() - 1) {
        return rowMonth <= currentMonth
      }
      
      return false
    })
  }

  // --- Top 10 Countries ---
  const countryMap = {}
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()

  // Appliquer les filtres dynamiques selon la configuration
  const countriesData = applyDynamicFilters(baseFilteredData, 'countries', {
    products: products,
    clients: clients
  })

  // Appliquer le filtre YTD si nécessaire
  const countriesDataFiltered = filterYTDData(countriesData)

  countriesDataFiltered.forEach(row => {
    const rowDate = new Date(row.order_invoice_date)
    const year = rowDate.getFullYear().toString()
    const month = rowDate.getMonth()

    // Pour YTD, ne garder que les lignes jusqu’au mois courant pour THIS_YEAR et LAST_YEAR
    if (period === "YTD") {
      if (!(
        (year === THIS_YEAR && month <= currentMonth) ||
        (year === LAST_YEAR && month <= currentMonth)
      )) {
        return
      }
    } else {
      if (year !== THIS_YEAR && year !== LAST_YEAR) return
    }

    const country = row.buyer_country || "Unknown"
    if (!countryMap[country]) {
      countryMap[country] = { country, [THIS_YEAR]: 0, [LAST_YEAR]: 0 }
    }

    const amount = parseFloat(row.order_item_amount_cents_cons_eur) || 0
    if (year === THIS_YEAR) countryMap[country][THIS_YEAR] += amount
    if (year === LAST_YEAR) countryMap[country][LAST_YEAR] += amount
  })

  const topCountries = Object.values(countryMap)
    .map(row => {
      const thisYear = row[THIS_YEAR] || 0
      const lastYear = row[LAST_YEAR] || 0
      return {
        country: row.country,
        [THIS_YEAR]: thisYear,
        [LAST_YEAR]: lastYear,
        Total: thisYear,
        varPct: getVarPct(thisYear, lastYear),
      }
    })
    .sort((a, b) => b[THIS_YEAR] - a[THIS_YEAR])
    .slice(0, limit)

  // --- Top 10 Clients ---
  const clientMap = {}
  // Appliquer les filtres dynamiques selon la configuration
  const clientsData = applyDynamicFilters(baseFilteredData, 'clients', {
    products: products,
    countries: countries
  })

  // Appliquer le filtre YTD si nécessaire
  const clientsDataFiltered = filterYTDData(clientsData)

  clientsDataFiltered.forEach(row => {
    const rowDate = new Date(row.order_invoice_date)
    const year = rowDate.getFullYear().toString()
    const month = rowDate.getMonth()

    if (period === "YTD") {
      if (!(
        (year === THIS_YEAR && month <= currentMonth) ||
        (year === LAST_YEAR && month <= currentMonth)
      )) {
        return
      }
    } else {
      if (year !== THIS_YEAR && year !== LAST_YEAR) return
    }

    const client = row.buyer_p3 || row.buyer_name || "Unknown Client"
    if (!clientMap[client]) {
      clientMap[client] = { client, [THIS_YEAR]: 0, [LAST_YEAR]: 0 }
    }

    const amount = parseFloat(row.order_item_amount_cents_cons_eur) || 0
    if (year === THIS_YEAR) clientMap[client][THIS_YEAR] += amount
    if (year === LAST_YEAR) clientMap[client][LAST_YEAR] += amount
  })

  const topClients = Object.values(clientMap)
    .map(row => {
      const thisYear = row[THIS_YEAR] || 0
      const lastYear = row[LAST_YEAR] || 0
      return {
        client: row.client,
        [THIS_YEAR]: thisYear,
        [LAST_YEAR]: lastYear,
        Total: thisYear,
        varPct: getVarPct(thisYear, lastYear),
      }
    })
    .sort((a, b) => b[THIS_YEAR] - a[THIS_YEAR])
    .slice(0, limit)

  // --- Top 10 Products by Quantities ---
  const productQuantityMap = {}
  // Appliquer les filtres dynamiques selon la configuration
  const productsData = applyDynamicFilters(baseFilteredData, 'products', {
    countries: countries,
    clients: clients
  })

  // Appliquer le filtre YTD si nécessaire
  const productsDataFiltered = filterYTDData(productsData)

  productsDataFiltered.forEach(row => {
    const rowDate = new Date(row.order_invoice_date)
    const year = rowDate.getFullYear().toString()
    const month = rowDate.getMonth()

    if (period === "YTD") {
      if (!(
        (year === THIS_YEAR && month <= currentMonth) ||
        (year === LAST_YEAR && month <= currentMonth)
      )) {
        return
      }
    } else {
      if (year !== THIS_YEAR && year !== LAST_YEAR) return
    }

    const product = row.product_name || "Unknown Product"
    if (!productQuantityMap[product]) {
      productQuantityMap[product] = {
        product,
        [THIS_YEAR]: 0,
        [LAST_YEAR]: 0,
        image: row.product_main_picture_url
      }
    }

    const quantity = parseFloat(row.order_item_quantity) || 0
    if (year === THIS_YEAR) productQuantityMap[product][THIS_YEAR] += quantity
    if (year === LAST_YEAR) productQuantityMap[product][LAST_YEAR] += quantity
  })

  const topProducts = Object.values(productQuantityMap)
    .map(row => {
      const thisYear = row[THIS_YEAR] || 0
      const lastYear = row[LAST_YEAR] || 0
      return {
        product: row.product,
        image: row.image,
        [THIS_YEAR]: thisYear,
        [LAST_YEAR]: lastYear,
        Total: thisYear,
        varPct: getVarPct(thisYear, lastYear),
      }
    })
    .sort((a, b) => b[THIS_YEAR] - a[THIS_YEAR])
    .slice(0, limit)

  // --- Top 10 Products by Sales Amount ---
  const productSalesMap = {}
  productsDataFiltered.forEach(row => {
    const rowDate = new Date(row.order_invoice_date)
    const year = rowDate.getFullYear().toString()
    const month = rowDate.getMonth()

    if (period === "YTD") {
      if (!(
        (year === THIS_YEAR && month <= currentMonth) ||
        (year === LAST_YEAR && month <= currentMonth)
      )) {
        return
      }
    } else {
      if (year !== THIS_YEAR && year !== LAST_YEAR) return
    }

    const product = row.product_name || "Unknown Product"
    if (!productSalesMap[product]) {
      productSalesMap[product] = {
        product,
        [THIS_YEAR]: 0,
        [LAST_YEAR]: 0,
        image: row.product_main_picture_url
      }
    }

    const amount = parseFloat(row.order_item_amount_cents_cons_eur) || 0
    if (year === THIS_YEAR) productSalesMap[product][THIS_YEAR] += amount
    if (year === LAST_YEAR) productSalesMap[product][LAST_YEAR] += amount
  })

  const topProductsBySales = Object.values(productSalesMap)
    .map(row => {
      const thisYear = row[THIS_YEAR] || 0
      const lastYear = row[LAST_YEAR] || 0
      return {
        product: row.product,
        image: row.image,
        [THIS_YEAR]: thisYear,
        [LAST_YEAR]: lastYear,
        Total: thisYear,
        varPct: getVarPct(thisYear, lastYear),
      }
    })
    .sort((a, b) => b[THIS_YEAR] - a[THIS_YEAR])
    .slice(0, limit)

  console.log('PERIOD:', period, 'THIS_YEAR:', THIS_YEAR, 'LAST_YEAR:', LAST_YEAR);
  console.log('Sample products:', topProducts); 
  console.log('Sample products by sales:', topProductsBySales);

  return {
    topCountries,
    topClients,
    topProducts,
    topProductsBySales,
    THIS_YEAR,
    LAST_YEAR,
  }
}

export default buildTop10ACollection
